import * as store from '@attain-apps/fe/web/main/lib/store'
import { Word } from '@attain-apps/string-builder'

import type * as cytoscape from './cytoscape'
import * as models from './models'
import { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig(
  /**
   * __NOTE:__ we type this as potentially returning `undefined` because it is
   * not instantiated at first
   */
  manager: () => cytoscape.Manager | undefined,
) {
  return {
    /* eslint-disable sort-keys */
    /* -------------------------------------------------------------------------- */
    /*                                    Core                                    */
    /* -------------------------------------------------------------------------- */
    /* -------------------------------- Inserting ------------------------------- */
    addNode: store.actions.standalone({
      data: (
        params: models.OrgChart.Identifier & {
          readonly info?: Partial<models.Node>
          readonly parentId?: models.Node.Id
        },
      ) => ({
        ...params,
        node: {
          ...params.info,
          id: params.info?.id ?? Word.generate(),
          name: params.info?.name ?? '???',
          type: params.info?.type ?? 'default',
        },
        parentId:
          params?.parentId ??
          manager()?.getSelectedNodeId({ orgChartId: params.orgChartId }),
      }),
      reducer: ({ state, data: { orgChartId, node, parentId } }) =>
        State.applyChangeToOrgChart({
          change: models.Change.of({
            edges: {
              added: parentId ? [{ childId: node.id, parentId }] : undefined,
            },
            nodes: {
              added: [node],
            },
          }),
          orgChartId,
          state,
        }),
    }),
    putNode: store.actions.standalone({
      data: store.actions.Data.asIs<
        models.OrgChart.Identifier & { readonly node: models.Node }
      >(),
      reducer: ({ state, data: { orgChartId, node } }) =>
        State.applyChangeToOrgChart({
          change: orgChart =>
            models.Change.of({
              nodes: {
                [orgChart?.nodes.some(
                  existingNode => existingNode.id === node.id,
                )
                  ? 'edited'
                  : 'added']: [node],
              },
            }),
          orgChartId,
          state,
        }),
    }),
    /* --------------------------------- Editing -------------------------------- */
    editNode: store.actions.standalone({
      data: (
        params: NodeSelector & {
          readonly edits?: Partial<Omit<models.Node, 'id'>>
        },
      ) => ({
        ...params,
        id:
          params?.id ??
          manager()?.getSelectedNodeId({ orgChartId: params.orgChartId }),
      }),
      reducer: ({ state, data: { orgChartId, edits, id } }) =>
        id === undefined
          ? state
          : State.patchOrgChart({
              apply: orgChart => {
                if (!orgChart) {
                  return
                }
                const editedNode =
                  orgChart.edits[id] ??
                  orgChart.nodes.find(node => node.id === id)
                if (!editedNode) {
                  return
                }
                return {
                  ...orgChart,
                  currentlyEditingNodeId: id ?? orgChart.currentlyEditingNodeId,
                  edits: {
                    ...orgChart.edits,
                    [id]: { ...editedNode, ...edits },
                  },
                }
              },
              orgChartId,
              state,
            }),
    }),
    saveEdits: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
      reducer: ({ state, data: { orgChartId } }) => {
        state = State.applyChangeToOrgChart({
          change: orgChart => {
            const nodeId = orgChart?.currentlyEditingNodeId
            if (!nodeId) {
              return
            }
            const to = orgChart?.edits[nodeId]
            if (!to) {
              return
            }
            const from = orgChart?.nodes.find(node => node.id === nodeId)
            if (!from) {
              return
            }
            return models.Change.of({ nodes: { edited: [{ from, to }] } })
          },
          orgChartId,
          state,
        })
        // NOTE: the above function makes this mutable
        delete state.orgCharts[orgChartId]?.currentlyEditingNodeId
        return state
      },
    }),
    cancelEdits: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
      reducer: ({ state, data: { orgChartId } }) =>
        State.patchOrgChart({
          apply: orgChart =>
            orgChart && {
              ...orgChart,
              currentlyEditingNodeId: undefined,
            },
          orgChartId,
          state,
        }),
    }),
    /* -------------------------------- Deletion -------------------------------- */
    deleteNode: store.actions.standalone({
      data: (params: NodeSelector) => ({
        ...params,
        id:
          params?.id ??
          manager()?.getSelectedNodeId({ orgChartId: params.orgChartId }),
      }),
      reducer: ({ state, data: { orgChartId, id } }) =>
        id === undefined
          ? state
          : State.applyChangeToOrgChart({
              change: orgChart => {
                const nodesToDelete = orgChart?.nodes.filter(
                  node => node.id === id,
                )
                const edgesToDelete = orgChart?.edges.filter(
                  edge => edge.parentId === id || edge.childId === id,
                )
                return models.Change.of({
                  edges: { deleted: edgesToDelete },
                  nodes: { deleted: nodesToDelete },
                })
              },
              orgChartId,
              state,
            }),
    }),
    /* ------------------------------- Assignment ------------------------------- */
    assign: store.actions.standalone({
      data: store.actions.Data.asIs<
        models.OrgChart.Identifier & {
          readonly edge: models.Edge
        }
      >(),
      reducer: ({ state, data: { orgChartId, edge } }) =>
        State.applyChangeToOrgChart({
          change: orgChart =>
            orgChart?.edges.some(
              existingEdge =>
                existingEdge.parentId === edge.parentId &&
                existingEdge.childId === edge.childId,
            )
              ? undefined
              : models.Change.of({
                  edges: {
                    added: [edge],
                    deleted: orgChart?.edges.filter(existingEdge => {
                      if (existingEdge.childId === edge.childId) {
                        // Removes existing edges to this child since it is being
                        // reassigned to a new parent
                        return true
                      }
                      if (existingEdge.parentId === edge.childId) {
                        const visited = new Set<string>()
                        // Removes any edges from this child that would cause a
                        // cycle with the new edge
                        const descendants = [existingEdge]
                        let descendant: models.Edge | undefined
                        while ((descendant = descendants.pop())) {
                          if (descendant.childId === edge.parentId) {
                            return true
                          }
                          visited.add(descendant.childId)
                          descendants.push(
                            ...orgChart.edges.filter(
                              potentialDescendant =>
                                potentialDescendant.parentId ===
                                  descendant?.childId &&
                                !visited.has(potentialDescendant.childId),
                            ),
                          )
                        }
                      }
                      // Doesn't remove anything else
                      return false
                    }),
                  },
                }),
          orgChartId,
          state,
        }),
    }),
    /* -------------------------------- Undo/Redo ------------------------------- */
    undo: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
      reducer: ({ state, data: { orgChartId } }) =>
        State.moveThroughHistoryInOrgChart({
          steps: 1,
          orgChartId,
          state,
        }),
    }),
    redo: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
      reducer: ({ state, data: { orgChartId } }) =>
        State.moveThroughHistoryInOrgChart({
          steps: -1,
          orgChartId,
          state,
        }),
    }),
    /* -------------------------------- Clean Up -------------------------------- */
    clear: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
      reducer: ({ state, data: { orgChartId } }) =>
        State.applyChangeToOrgChart({
          change: orgChart =>
            orgChart &&
            models.Change.of({
              edges: {
                deleted: orgChart.edges,
              },
              nodes: {
                deleted: orgChart.nodes,
              },
            }),
          orgChartId,
          state,
        }),
    }),
    cleanUp: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
    }),
    /* -------------------------------------------------------------------------- */
    /*                                  Proposals                                 */
    /* -------------------------------------------------------------------------- */
    propose: store.actions.standalone({
      data: store.actions.Data.asIs<
        | models.Data
        | Exclude<models.OrgChart.Id, Satisfies<'proposed', models.OrgChart.Id>>
      >(),
      reducer: ({ state, data }) => ({
        ...state,
        orgCharts: {
          ...state.orgCharts,
          proposed:
            typeof data === 'string'
              ? state.orgCharts[data]
              : {
                  ...data,
                  edits: {},
                  history: [],
                  undoCount: 0,
                },
        },
      }),
    }),
    approveProposal: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => {
        const { current, proposed } = state.orgCharts
        if (!proposed) {
          return state
        }
        // NOTE: this adds this change to the history so it can be undone
        state = State.applyChangeToOrgChart({
          change: models.Change.of({
            edges: {
              added: proposed.edges,
              deleted: current.edges,
            },
            nodes: {
              added: proposed.nodes,
              deleted: current.nodes,
            },
          }),
          orgChartId: 'current',
          state,
        })
        // Deletes the proposal, since now it has been applied to the current
        // org chart
        state.orgCharts = {
          ...state.orgCharts,
          proposed: undefined,
        }
        return state
      },
    }),
    rejectProposal: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => ({
        ...state,
        orgCharts: {
          ...state.orgCharts,
          proposed: undefined,
        },
      }),
    }),
    /* -------------------------------------------------------------------------- */
    /*                             Instance Management                            */
    /* -------------------------------------------------------------------------- */
    bind: store.actions.standalone({
      data: store.actions.Data.asIs<models.Binding>(),
    }),
    destroy: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
    }),
    /* -------------------------------------------------------------------------- */
    /*                                Local Storage                               */
    /* -------------------------------------------------------------------------- */
    save: store.actions.standalone({
      data: store.actions.Data.asIs<models.OrgChart.Identifier>(),
    }),
    load: store.actions.request({
      input: store.actions.Data.asIs<models.OrgChart.Identifier>(),
      output: store.actions.Data.asIs<models.Data>(),
      reducers: {
        success: ({
          state,
          data: {
            request: { orgChartId },
            response: data,
          },
        }) =>
          State.patchOrgChart({
            apply: {
              ...data,
              edits: {},
              history: [],
              undoCount: 0,
            },
            orgChartId,
            state,
          }),
      },
    }),
    /* eslint-enable sort-keys */
  } as const satisfies store.actions.Config.Constraint<State>
}

type NodeSelector = models.OrgChart.Identifier & {
  /**
   * @default currently selected ID
   */
  readonly id?: models.Node.Id
}
